// Piezas visuales del shell «Orion» — solo tokens semánticos (bg-card,
// text-foreground, …); nada de colores fijos.

import type { ReactNode } from 'react';
import { ArrowDownRight, ArrowUpRight, Loader2, Minus } from 'lucide-react';

export function Card({
  title,
  action,
  children,
  className = '',
  bodyClassName = '',
  titleId,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
  titleId?: string;
}) {
  return (
    <section
      aria-labelledby={titleId}
      className={`orion-card flex min-w-0 flex-col rounded-[20px] border border-border bg-card text-card-foreground ${className}`}
    >
      {(title || action) && (
        <header className="flex items-center gap-3 px-5 pb-1 pt-5">
          {title && (
            <h2 id={titleId} className="min-w-0 flex-1 truncate text-base font-semibold tracking-tight text-foreground">
              {title}
            </h2>
          )}
          {action}
        </header>
      )}
      <div className={`min-w-0 flex-1 px-5 pb-5 pt-3 ${bodyClassName}`}>{children}</div>
    </section>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end">
      <div className="min-w-0 flex-1">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-[30px]">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border px-4 py-6 text-center">
      {icon && <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">{icon}</div>}
      <p className="text-sm font-medium text-foreground">{title}</p>
      {children && <p className="max-w-xs text-xs leading-relaxed text-muted-foreground">{children}</p>}
      {action}
    </div>
  );
}

export function Loading({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground" role="status">
      <Loader2 size={16} className="animate-spin" aria-hidden /> {label}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{children}</p>;
}

export function Button({
  children,
  onClick,
  variant = 'secondary',
  disabled,
  type = 'button',
  className = '',
  ...rest
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'brand' | 'secondary' | 'ghost' | 'danger';
  disabled?: boolean;
  type?: 'button' | 'submit';
  className?: string;
  'aria-label'?: string;
  'aria-expanded'?: boolean;
}) {
  const base = 'inline-flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50';
  const v = {
    primary: 'bg-primary text-primary-foreground hover:opacity-90',
    brand: 'orion-btn-brand font-semibold',
    secondary: 'border border-border bg-card text-foreground hover:bg-muted',
    ghost: 'text-foreground hover:bg-muted',
    danger: 'border border-destructive/40 bg-card text-destructive hover:bg-destructive/10',
  }[variant];
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${v} ${className}`} {...rest}>
      {children}
    </button>
  );
}

/** Variación periodo actual vs anterior. Sin datos de comparación → nada. */
export function Delta({ current, previous }: { current: number; previous: number }) {
  if (previous === 0 && current === 0) return null;
  if (previous === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">
        Nuevo en el periodo
      </span>
    );
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  const up = pct > 0;
  const flat = pct === 0;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground"
      title={`Periodo anterior: ${previous}`}
    >
      <Icon size={12} aria-hidden />
      {flat ? 'Igual' : `${up ? '+' : ''}${pct}%`}
      <span className="sr-only"> frente al periodo anterior ({previous})</span>
    </span>
  );
}

/** Mini tendencia (SVG). Decorativa: el número y la variación ya lo dicen. */
export function Sparkline({ data }: { data: number[] }) {
  if (data.length < 2) data = [0, 0];
  if (data.every((v) => v === 0)) {
    // Sin actividad: línea base plana y tenue (no un hueco).
    return (
      <svg viewBox="0 0 96 28" preserveAspectRatio="none" className="h-7 w-full sm:w-24" aria-hidden focusable="false">
        <line x1="0" y1="25" x2="96" y2="25" stroke="hsl(var(--muted-foreground) / 0.35)" strokeWidth="2" strokeLinecap="round" strokeDasharray="1 5" />
      </svg>
    );
  }
  const w = 96;
  const h = 28;
  const max = Math.max(...data, 1);
  const step = w / (data.length - 1);
  const pts = data.map((v, i) => `${(i * step).toFixed(1)},${(h - 2 - (v / max) * (h - 4)).toFixed(1)}`);
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden focusable="false" className="h-7 w-full overflow-visible sm:w-24">
      <polyline points={`0,${h} ${pts.join(' ')} ${w},${h}`} fill="var(--tone, hsl(var(--primary)))" fillOpacity={0.12} stroke="none" />
      <polyline points={pts.join(' ')} fill="none" stroke="var(--tone, hsl(var(--primary)))" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

export function KpiCard({
  icon,
  label,
  value,
  sub,
  delta,
  spark,
  loading,
  href,
  onOpen,
  tone = 'violet',
}: {
  tone?: 'violet' | 'indigo' | 'blue' | 'magenta' | 'purple';
  icon: ReactNode;
  label: string;
  value: number | null;
  sub?: ReactNode;
  delta?: { current: number; previous: number } | null;
  spark?: number[] | null;
  loading?: boolean;
  href?: string;
  onOpen?: () => void;
}) {
  const body = (
    <>
      <div className="flex items-center gap-2 sm:gap-3">
        <span className={`orion-tone tone-${tone} flex h-9 w-9 shrink-0 items-center justify-center rounded-xl sm:h-11 sm:w-11`} aria-hidden>
          {icon}
        </span>
        <span className="min-w-0 flex-1 text-sm font-semibold leading-tight text-foreground">{label}</span>
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <div className="text-[26px] font-semibold sm:text-[30px] leading-none tracking-tight text-foreground tabular-nums">
            {loading ? <span className="inline-block h-7 w-12 animate-pulse rounded-md bg-muted align-middle" aria-label="Cargando" /> : value === null ? '—' : value.toLocaleString('es-MX')}
          </div>
          <div className="mt-2 flex min-h-[20px] flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {!loading && delta && <Delta current={delta.current} previous={delta.previous} />}
            {!loading && sub}
          </div>
        </div>
        {!loading && (
          <span className={`tone-${tone} block w-full shrink-0 sm:w-auto`}>
            <Sparkline data={spark ?? []} />
          </span>
        )}
      </div>
    </>
  );
  const cls =
    'orion-card block min-w-0 rounded-[20px] border border-border bg-card p-4 text-left transition-colors sm:p-5';
  if (onOpen) {
    return (
      <a
        href={href}
        onClick={(e) => {
          e.preventDefault();
          onOpen();
        }}
        className={`${cls} hover:border-primary/40 hover:bg-muted/40`}
      >
        {body}
      </a>
    );
  }
  return <div className={cls}>{body}</div>;
}
